import { fabric } from 'fabric';
import { isPhotozoneImage } from '../../../features/card-editor/utils/is-photozone-image';
import { FabricObject, FabricTextBox } from '../../../global-types';
import { CanvasDataTypes, helperSettingsConfig, isImage, isTextbox, renderSizeTextIcon } from '../../../utils';
import { CustomControlOptions } from '../canvas-custom-config-types';
import { renderCustomCroppingControl } from '../utils/render-custom-cropping-control';

const { scalingFactor } = helperSettingsConfig;

const renderImageControls = (
  icons: Record<string, HTMLImageElement>,
  controlName: string,
  ctx: CanvasRenderingContext2D,
  options: CustomControlOptions,
  control: fabric.Control,
) => {
  const { left, top, iconSize, size } = options;

  const icon = icons[`${controlName}`];

  if (!icon) {
    return;
  }

  ctx.save();
  ctx.translate(left, top);

  const actualSizeRotateIcon = iconSize ?? 0;
  switch (controlName) {
    case 'tr':
    case 'bl':
    case 'tl':
    case 'br':
      ctx.drawImage(icon, -size / scalingFactor, -size / scalingFactor, size, size);
      break;
    case 'mtr':
      renderSizeTextIcon(actualSizeRotateIcon, control, ctx, icon, scalingFactor);
      break;
  }
  ctx.restore();
};

const renderTextControl = (
  icons: Record<string, HTMLImageElement>,
  controlName: string,
  ctx: CanvasRenderingContext2D,
  options: CustomControlOptions,
  control: fabric.Control,
  textBox: FabricTextBox,
) => {
  const { left, top, size, iconSize } = options;
  const actualSizeTrash = iconSize ?? 0;

  const icon = icons[`${controlName}`];

  if (!icon) {
    return;
  }
  const shouldShowResizeControls =
    textBox.CanResizeTextArea ||
    textBox.data?.type === CanvasDataTypes.UserText ||
    textBox.data?.type === CanvasDataTypes.Placeholder;
  const shouldShowDeleteControls =
    (textBox.CanDeleteTextArea && textBox.data?.type === CanvasDataTypes.EditableText) ||
    textBox.data?.type === CanvasDataTypes.Placeholder;

  ctx.save();
  ctx.translate(left, top);

  switch (controlName) {
    case 'tr':
    case 'bl':
    case 'tl':
    case 'br':
      if (shouldShowResizeControls) {
        ctx.drawImage(icon, -size / 2, -size / 2, size, size);
      }
      break;
    case 'ml':
      if (shouldShowResizeControls) {
        ctx.drawImage(icon, -12 / scalingFactor, -size, size, size + 12);
      }
      break;
    case 'mr':
      if (shouldShowResizeControls) {
        ctx.drawImage(icon, -8 / scalingFactor, -size, size, size + 12);
      }
      break;
    case 'deleteControl':
      if (shouldShowDeleteControls || shouldShowResizeControls) {
        renderSizeTextIcon(actualSizeTrash, control, ctx, icon, scalingFactor);
      }
      break;
  }
  ctx.restore();
};

export const renderCustomControls = (
  icons: Record<string, HTMLImageElement>,
  controlName: string,
  ctx: CanvasRenderingContext2D,
  left: number,
  top: number,
  fabricObject: FabricObject,
  control: fabric.Control,
  isNewToolbarEnabled = false,
) => {
  const initialSize = 20;
  const sizeMultiplier = 1.8;
  const { angle = 0 } = fabricObject;

  const scaledSize = Math.min(initialSize, 10);

  const options = {
    left,
    top,
    size: scaledSize,
    iconSize: initialSize * sizeMultiplier,
    angle,
  };

  ctx.restore();
  if (isTextbox(fabricObject)) {
    renderTextControl(icons, controlName, ctx, options, control, fabricObject);
  }
  if (isImage(fabricObject)) {
    if (isPhotozoneImage(fabricObject)) {
      fabricObject.hasControls = false;
      return;
    }
    renderImageControls(icons, controlName, ctx, options, control);
  }
  if (fabricObject.name === 'cropping-rect') {
    renderCustomCroppingControl(controlName, ctx, left, top, fabricObject, isNewToolbarEnabled);
  }
};
